import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Rally } from '../../data/rallies'
import facebookIcon from 'url:../../assets/facebook.png'
import instagramIcon from 'url:../../assets/instagram.png'

// small icon for known social links; anything else shows 🔗
const linkIcons = [
  { host: 'facebook.com', icon: facebookIcon, labelKey: 'help.facebook' },
  { host: 'instagram.com', icon: instagramIcon, labelKey: 'help.instagram' },
]

interface Props {
  src: string
  city: string
  label?: string
  author?: string
  link?: string
  height?: string
  rally?: Rally
  downloadable?: boolean
}

export default function GalleryItem({
  src,
  city,
  label,
  author,
  link,
  height,
  rally,
  downloadable,
}: Props) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const ns = rally?.citiesI18n ?? 'cities'
  const monthShortKey =
    rally?.days.find((d) => d.cities.includes(city))?.monthShortKey ?? 'home.dec'
  const day = t(`${ns}.${city}.day`)
  const linkIcon = link ? linkIcons.find(({ host }) => link.includes(host)) : undefined

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '100px' },
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={`
        relative
        w-full
        overflow-hidden
        rounded-sm
        group
        ${height ?? 'aspect-3/4'}
      `}
    >
      {isVisible && (
        <img
          src={src}
          alt={
            label
              ? t(label)
              : city !== 'none'
                ? `${t(`${ns}.${city}.city`)} – ${t(`${ns}.${city}.location`)}`
                : (author ?? '')
          }
          className='
            w-full h-full
            object-contain
            transition-opacity duration-300
          '
        />
      )}

      {downloadable && (
        <a
          href={src}
          // save as the original file name, without the build hash (e.g. "London.0dff0907.jpg" -> "London.jpg")
          download={decodeURIComponent(src.split('/').pop() ?? '').replace(
            /\.[0-9a-f]{8}(\.\w+)$/,
            '$1',
          )}
          aria-label={t('rallies.downloadPoster')}
          title={t('rallies.downloadPoster')}
          className='absolute top-2 right-2 z-10 rounded-full bg-white/90 p-2 text-gray-900 shadow-md hover:bg-white cursor-pointer'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className='h-5 w-5'
            aria-hidden='true'
          >
            <path d='M12 3v12' />
            <path d='m7 10 5 5 5-5' />
            <path d='M5 21h14' />
          </svg>
        </a>
      )}

      {(label || author || city !== 'none') && (
        <div
          className='
          absolute inset-0
          bg-black/0 
          group-hover:bg-black/60
          transition-colors duration-300
          flex flex-col items-center justify-center p-2
        '
        >
          {[
            ...(label
              ? [t(label)]
              : city !== 'none'
                ? [
                    t(`${ns}.${city}.city`),
                    [`${t(`home.${day}`)} ${t(monthShortKey)}`, t(`${ns}.${city}.cleanTime`)]
                      .filter(Boolean)
                      .join(': '),
                    t(`${ns}.${city}.location`),
                  ]
                : []),
            ...(author ? [t('rallies.artBy', { name: author })] : []),
          ].map((line, i) => (
            <p
              key={line}
              className='
              text-white text-lg font-semibold
              opacity-0 
              group-hover:opacity-100
              transition-opacity duration-300
            '
            >
              {line}
              {link && i === 0 && (
                // only clickable while the overlay is showing
                <a
                  href={link}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={linkIcon ? t(linkIcon.labelKey) : link}
                  className='inline-block align-middle ml-2 pointer-events-none group-hover:pointer-events-auto hover:opacity-70'
                >
                  {linkIcon ? (
                    <img src={linkIcon.icon} alt={t(linkIcon.labelKey)} className='h-4 w-4' />
                  ) : (
                    '🔗'
                  )}
                </a>
              )}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
